import { roleHas, type MetaConnectionDto, type SyncJobDto } from "@iagenix/shared";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { createFileRoute, useNavigate } from "@tanstack/react-router";
import {
  AlertTriangle,
  CheckCircle2,
  Loader2,
  MoreHorizontal,
  Plug,
  RefreshCw,
} from "lucide-react";
import { useEffect, useState } from "react";
import { toast } from "sonner";
import { z } from "zod";
import { PageHeader } from "@/components/page-header";
import { EmptyState, ErrorState, LoadingState } from "@/components/states";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { api, errorMessage } from "@/lib/api";
import { queries } from "@/lib/queries";
import { formatDate, formatRelative } from "@/lib/utils";

const searchSchema = z.object({
  meta: z.enum(["conectada", "reconectada", "erro"]).optional(),
  motivo: z.string().optional(),
  faltando: z.string().optional(),
});

export const Route = createFileRoute("/app/$workspaceId/integracoes")({
  validateSearch: searchSchema,
  component: IntegracoesPage,
});

const STATUS: Record<
  MetaConnectionDto["status"],
  { label: string; variant: "success" | "warning" | "destructive" }
> = {
  ativa: { label: "Ativa", variant: "success" },
  expirada: { label: "Expirada", variant: "warning" },
  revogada: { label: "Revogada", variant: "destructive" },
};

const JOB_STATUS: Record<SyncJobDto["status"], string> = {
  pendente: "Na fila",
  executando: "Sincronizando…",
  concluido: "Concluída",
  erro: "Com erro (vai tentar de novo)",
  descartado: "Falhou",
};

const SCOPE_LABELS: Record<string, string> = {
  ads_read: "Ler anúncios",
  ads_management: "Gerenciar anúncios",
  business_management: "Gerenciar negócios",
  pages_show_list: "Listar páginas",
  pages_read_engagement: "Ler engajamento das páginas",
};

function IntegracoesPage() {
  const { workspaceId } = Route.useParams();
  const search = Route.useSearch();
  const navigate = useNavigate();
  const workspace = useQuery(queries.workspace(workspaceId));
  const status = useQuery(queries.metaStatus(workspaceId));
  const connections = useQuery(queries.metaConnections(workspaceId));
  const queryClient = useQueryClient();

  const myRole = workspace.data?.role;
  const podeGerenciar = myRole ? roleHas(myRole, "meta:manage") : false;
  const podeSincronizar = myRole ? roleHas(myRole, "meta:sync") : false;

  // Feedback do retorno do OAuth (vem na URL) vira toast e some da URL.
  useEffect(() => {
    if (!search.meta) return;
    if (search.meta === "erro") {
      toast.error(search.motivo ?? "Não foi possível conectar a conta Meta.");
    } else {
      toast.success(
        search.meta === "reconectada"
          ? "Conta Meta reconectada. Sincronizando os ativos…"
          : "Conta Meta conectada. Sincronizando os ativos…",
      );
      if (search.faltando) {
        toast.warning(
          `Permissões não concedidas: ${search.faltando}. Algumas funções podem não funcionar.`,
        );
      }
    }
    void queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });
    void navigate({
      to: "/app/$workspaceId/integracoes",
      params: { workspaceId },
      search: {},
      replace: true,
    });
  }, [search.meta, search.motivo, search.faltando, navigate, workspaceId, queryClient]);

  const conectar = (reconnect?: string) => {
    const q = reconnect ? `?reconnect=${encodeURIComponent(reconnect)}` : "";
    window.location.assign(`/api/workspaces/${workspaceId}/meta/connect${q}`);
  };

  return (
    <>
      <PageHeader
        title="Integrações Meta"
        description="Conecte perfis Meta para importar Business Managers, contas de anúncio, páginas e pixels."
        actions={
          podeGerenciar ? (
            <Button onClick={() => conectar()}>
              <Plug />
              Conectar conta Meta
            </Button>
          ) : null
        }
      />

      {status.data?.mode === "mock" ? (
        <Alert className="mb-6">
          <AlertTriangle />
          <AlertTitle>Modo simulado</AlertTitle>
          <AlertDescription>
            A API está em <code>META_MODE=mock</code>: o login e os ativos são de uma Graph API
            falsa. Nada aqui fala com a Meta de verdade.
          </AlertDescription>
        </Alert>
      ) : null}

      <Card>
        <CardHeader>
          <CardTitle>Conexões</CardTitle>
          <CardDescription>
            Cada conexão é um perfil Meta. Os ativos são sincronizados ao conectar e a cada hora.
          </CardDescription>
        </CardHeader>
        <CardContent>
          {connections.isPending ? (
            <LoadingState rows={2} />
          ) : connections.isError ? (
            <ErrorState error={connections.error} onRetry={() => void connections.refetch()} />
          ) : connections.data.length === 0 ? (
            <EmptyState
              icon={<Plug className="size-8" aria-hidden />}
              title="Nenhuma conta Meta conectada"
              description={
                podeGerenciar
                  ? "Conecte um perfil Meta com acesso aos Business Managers e contas que você gerencia."
                  : "Peça a um Dono ou Admin do workspace para conectar uma conta Meta."
              }
              action={
                podeGerenciar ? (
                  <Button onClick={() => conectar()}>
                    <Plug />
                    Conectar conta Meta
                  </Button>
                ) : undefined
              }
            />
          ) : (
            <ul className="divide-y">
              {connections.data.map((c) => (
                <ConnectionRow
                  key={c.id}
                  connection={c}
                  workspaceId={workspaceId}
                  podeGerenciar={podeGerenciar}
                  podeSincronizar={podeSincronizar}
                  onReconnect={() => conectar(c.id)}
                />
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </>
  );
}

function ConnectionRow({
  connection: c,
  workspaceId,
  podeGerenciar,
  podeSincronizar,
  onReconnect,
}: {
  connection: MetaConnectionDto;
  workspaceId: string;
  podeGerenciar: boolean;
  podeSincronizar: boolean;
  onReconnect: () => void;
}) {
  const queryClient = useQueryClient();
  const [confirmarDesconectar, setConfirmarDesconectar] = useState(false);
  const invalidate = () =>
    queryClient.invalidateQueries({ queryKey: ["workspaces", workspaceId, "meta"] });

  const sincronizar = useMutation({
    mutationFn: () =>
      api<SyncJobDto>(`/api/workspaces/${workspaceId}/meta/connections/${c.id}/sync`, {
        method: "POST",
      }),
    onSuccess: async () => {
      await invalidate();
      toast.success("Sincronização iniciada.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const verificar = useMutation({
    mutationFn: () =>
      api<{ valid: boolean; reason: string | null }>(
        `/api/workspaces/${workspaceId}/meta/connections/${c.id}/verify`,
        { method: "POST" },
      ),
    onSuccess: async (r) => {
      await invalidate();
      if (r.valid) toast.success("Token válido.");
      else toast.warning(r.reason ?? "Reconectar conta Meta.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });
  const desconectar = useMutation({
    mutationFn: () =>
      api(`/api/workspaces/${workspaceId}/meta/connections/${c.id}`, { method: "DELETE" }),
    onSuccess: async () => {
      setConfirmarDesconectar(false);
      await invalidate();
      toast.success("Conta Meta desconectada.");
    },
    onError: (err) => toast.error(errorMessage(err)),
  });

  const status = STATUS[c.status];
  const job = c.lastJob;
  const sincronizando = job?.status === "pendente" || job?.status === "executando";
  const ocupado = sincronizar.isPending || verificar.isPending || desconectar.isPending;

  return (
    <li className="flex flex-col gap-3 py-4 sm:flex-row sm:items-start">
      <div className="min-w-0 flex-1 space-y-2">
        <div className="flex flex-wrap items-center gap-2">
          <p className="font-medium">{c.name}</p>
          <Badge variant={status.variant}>{status.label}</Badge>
          {sincronizando ? (
            <Badge variant="secondary">
              <Loader2 className="mr-1 size-3 animate-spin" aria-hidden />
              {JOB_STATUS[job.status]}
            </Badge>
          ) : null}
        </div>
        <p className="text-xs text-muted-foreground">
          {c.email ? `${c.email} · ` : ""}id Meta {c.metaUserId}
          {c.connectedByName ? ` · conectada por ${c.connectedByName}` : ""} em{" "}
          {formatDate(c.createdAt)}
        </p>
        {c.status !== "ativa" && c.statusReason ? (
          <Alert variant="destructive">
            <AlertTriangle />
            <AlertTitle>Reconectar conta Meta</AlertTitle>
            <AlertDescription>{c.statusReason}</AlertDescription>
          </Alert>
        ) : null}
        <dl className="grid gap-x-6 gap-y-1 text-sm sm:grid-cols-2">
          <div>
            <dt className="text-xs text-muted-foreground">Última sincronização</dt>
            <dd>
              {c.lastSyncedAt ? (
                <span title={formatDate(c.lastSyncedAt)}>{formatRelative(c.lastSyncedAt)}</span>
              ) : (
                "ainda não sincronizou"
              )}
              {job && !sincronizando ? (
                <span className="text-muted-foreground"> · {JOB_STATUS[job.status]}</span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Token</dt>
            <dd>
              {c.tokenExpiresAt ? `expira em ${formatDate(c.tokenExpiresAt)}` : "não expira"}
              {c.lastVerifiedAt ? (
                <span className="text-muted-foreground">
                  {" "}
                  · verificado {formatRelative(c.lastVerifiedAt)}
                </span>
              ) : null}
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Ativos</dt>
            <dd>
              {c.counts.businesses} BM · {c.counts.adAccounts} contas · {c.counts.pages} páginas ·{" "}
              {c.counts.pixels} pixels
            </dd>
          </div>
          <div>
            <dt className="text-xs text-muted-foreground">Permissões concedidas</dt>
            <dd className="flex flex-wrap gap-1">
              {c.scopes.length === 0 ? (
                <span className="text-muted-foreground">nenhuma</span>
              ) : (
                c.scopes.map((s) => (
                  <Badge key={s} variant="outline" title={s}>
                    {SCOPE_LABELS[s] ?? s}
                  </Badge>
                ))
              )}
            </dd>
          </div>
        </dl>
        {job?.lastError && job.status !== "concluido" ? (
          <p className="text-sm text-destructive">Erro na sincronização: {job.lastError}</p>
        ) : null}
        {job?.result && job.result.warnings.length > 0 ? (
          <ul className="list-disc pl-5 text-xs text-amber-700 dark:text-amber-400">
            {job.result.warnings.map((w) => (
              <li key={w}>{w}</li>
            ))}
          </ul>
        ) : null}
      </div>

      <div className="flex shrink-0 items-center gap-2">
        {podeSincronizar && c.status === "ativa" ? (
          <Button
            variant="outline"
            size="sm"
            onClick={() => sincronizar.mutate()}
            loading={sincronizar.isPending}
            disabled={sincronizando}
          >
            <RefreshCw />
            Sincronizar agora
          </Button>
        ) : null}
        {podeGerenciar && c.status !== "ativa" ? (
          <Button size="sm" onClick={onReconnect}>
            <Plug />
            Reconectar
          </Button>
        ) : null}
        {podeGerenciar || podeSincronizar ? (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button
                variant="ghost"
                size="icon"
                aria-label={`Ações para ${c.name}`}
                loading={ocupado}
              >
                <MoreHorizontal />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              {podeSincronizar ? (
                <DropdownMenuItem onSelect={() => verificar.mutate()}>
                  <CheckCircle2 />
                  Verificar token agora
                </DropdownMenuItem>
              ) : null}
              {podeGerenciar ? (
                <>
                  <DropdownMenuItem onSelect={onReconnect}>
                    <Plug />
                    Reconectar
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                  <DropdownMenuItem
                    variant="destructive"
                    onSelect={() => setConfirmarDesconectar(true)}
                  >
                    Desconectar
                  </DropdownMenuItem>
                </>
              ) : null}
            </DropdownMenuContent>
          </DropdownMenu>
        ) : null}
      </div>

      <Dialog open={confirmarDesconectar} onOpenChange={setConfirmarDesconectar}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Desconectar {c.name}?</DialogTitle>
            <DialogDescription>
              Revogamos a permissão na Meta, apagamos o token e removemos deste workspace os
              Business Managers, contas, páginas e pixels importados por esta conexão. Nada é
              alterado na Meta.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setConfirmarDesconectar(false)}>
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={() => desconectar.mutate()}
              loading={desconectar.isPending}
            >
              Desconectar
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </li>
  );
}
